import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Option } from 'partnerhub';
import { Observable, of } from 'rxjs';
import { map, pluck } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  City,
  Condition,
  ContentType,
  Country,
  Language,
  Offer,
  OfferType,
  Operator,
  Period,
  Region,
  ResourceType,
  State,
} from '../../../shared/models';

@Injectable({
  providedIn: 'root',
})
export class GeneralService {
  public baseURL = environment.serverApiBaseUrl;
  constructor(private readonly http: HttpClient) {}

  public getBenefitTypes(): Observable<Option[]> {
    const url = `/mock/benefit-types`;

    return this.http.get<Option[]>(url).pipe(pluck('responseData'));
  }

  public getBundleTypes(): Observable<any[]> {
    const url = `/api/webapp-partnerhub-admin/bundles/types`;

    return this.http.get<any[]>(url).pipe(pluck('responseData'));
  }

  public getCities(): Observable<City[]> {
    const url = `/mock/city`;

    return this.http.get<City[]>(url).pipe(pluck('responseData'));
  }

  public getConditions(): Observable<Condition[]> {
    const url = `/mock/conditionTypes`;

    return this.http.get<Condition[]>(url).pipe(pluck('responseData'));
  }

  public getContentTypes(): Observable<ContentType[]> {
    const url = `/api/webapp-partnerhub-admin/products/content-types`;

    return this.http.get<ContentType[]>(url).pipe(pluck('responseData'));
  }

  public getCountries(): Observable<Country[]> {
    const url = `/api/webapp-partnerhub-admin/countries?limit=10000`;

    return this.http.get<Country[]>(url).pipe(pluck('responseData'));
  }

  public getCountriesWithOperators(): Observable<Country[]> {
    const url = `/api/webapp-partnerhub-admin/countries/withOperators?limit=10000`;

    return this.http.get<Country[]>(url).pipe(pluck('responseData'));
  }

  public getDocsOffer(): Observable<Option[]> {
    // BE: Hardcoded query params
    const url = `/api/webapp-partnerhub-admin/documents/v2?entityType=offer&entityId=123&categoryId=7`;

    return this.http.get<Option[]>(url).pipe(pluck('responseData'));
  }

  public getEntryChannelOptions(): Observable<Option[]> {
    // BE: Waiting for Endpoint
    return of([
      { id: 1, value: 'SMS' },
      { id: 2, value: 'Vivo 360' },
      { id: 3, value: 'URA' },
      { id: 4, value: 'Web do Parceiro' },
      { id: 5, value: 'Aplicativo' },
      { id: 6, value: 'VivoChip' },
      { id: 7, value: 'AppStore' },
      { id: 8, value: 'SatPush/Outros Canais' },
      // { id: 3, value: 'USSD' },
      // { id: 7, value: 'MMS' },
      // { id: 4, value: 'DIGITAL / INAPP' },
      // { id: 2, value: 'IVR' },
      // { id: 6, value: 'SAT-PUSH' },
    ]);
  }

  public getEventTypes(): Observable<Option[]> {
    const url = `/mock/events`;

    return this.http.get<Option[]>(url).pipe(pluck('responseData'));
  }

  public getLanguages(): Observable<Option[]> {
    const url = `/api/webapp-partnerhub-admin/languages/?limit=10000`;

    return this.http.get<Language[]>(url).pipe(
      pluck('responseData'),
      map((languages: Language[]) => {
        return languages.map((language) => ({ id: language.id, name: language.locale }));
      })
    );
  }

  public getOffers(): Observable<Offer[]> {
    const url = `/api/webapp-partnerhub-admin/offers`;

    return this.http.get<Offer[]>(url).pipe(pluck('responseData'));
  }

  public getOfferTypes(): Observable<OfferType[]> {
    const url = `/api/webapp-partnerhub-admin/offers/offer-types`;

    return this.http.get<OfferType[]>(url).pipe(pluck('responseData'));
  }

  public getOperations(): Observable<Option[]> {
    const url = `/api/webapp-partnerhub-admin/operations`;

    return this.http.get<Option[]>(url).pipe(pluck('responseData'));
  }

  public getOperatorsByCountryId(countryId: number): Observable<Operator[]> {
    const url = `/api/webapp-partnerhub-admin/countries/${countryId}/operators?limit=10000`;

    return this.http.get<Operator[]>(url).pipe(pluck('responseData'));
  }

  public getPaymentMethodOptions(): Observable<Option[]> {
    // BE: Waiting for Endpoint

    return of([
      { id: 'carrierBilling', value: 'Carrier Billing' },
      { id: 'creditCard', value: 'Credit Card' },
      { id: 'wallet', value: 'Wallet' },
    ]);
  }

  public getPeriods(): Observable<Period[]> {
    const url = `/api/webapp-partnerhub-admin/enumerations/period`;

    return this.http.get<Period[]>(url).pipe(pluck('responseData'));
  }

  public getPlans(): Observable<any[]> {
    const url = `/mock/bundlePlans`;

    return this.http.get<any[]>(url).pipe(pluck('responseData'));
  }

  public getRegions(): Observable<Region[]> {
    const url = `/mock/region`;

    return this.http.get<Region[]>(url).pipe(pluck('responseData'));
  }

  public getResourceTypes() {
    const url = `/${this.baseURL}/product/type`;
    return this.http.get(url).pipe(
      map((response: any) => {
        response = response.map((type) => ({
          id: type,
          name: type,
          label: type,
          value: type,
        }));
        response.unshift({ label: 'QUALQUER', name: 'QUALQUER', id: 'QUALQUER', value: '' });
        return response;
      })
    );
  }

  public getResourceOfferTypes() {
    const url = `/${this.baseURL}/offer/type`;
    return this.http.get(url).pipe(
      map((response: any) => {
        response = response.filter(type => type === 'SUBSCRIPTION' || type === 'ONE_SHOT').map((type) => ({
          id: type,
          name: type,
          label: type,
          value: type,
        }));
        response.unshift({ label: 'QUALQUER', name: 'QUALQUER', id: 'QUALQUER', value: '' });
        return response;
      })
    );
  }

  public getResourceOfferTypesBundle() {
    const url = `/${this.baseURL}/offer/type`;
    return this.http.get(url).pipe(
      map((response: any) => {
        response = response.filter(type => type === 'SUBSCRIPTION').map((type) => ({
          id: type,
          name: type,
          label: type,
          value: type,
        }));
        response.unshift({ 
          label: 'QUALQUER', 
          name: 'QUALQUER', 
          id: 'QUALQUER', 
          value: '' 
        });
        return response;
      })
    );
  }

  public getShortCodeOptions(): Observable<Option[]> {
    // BE: Waiting for endpont
    return of([
      { id: '1', value: '1111', name: '1111' },
      { id: '2', value: '2222', name: '2222' },
      { id: '3', value: '3333', name: '3333' },
    ]);
  }

  public getState(): Observable<State[]> {
    const url = `/mock/state`;

    return this.http.get<State[]>(url).pipe(pluck('responseData'));
  }

  public getSubtype(): Observable<any[]> {
    return of([
      { id: 1, name: 'Produtos 2.0' },
      { id: 2, name: 'Direct Carrier Billing (DCB)' },
      { id: 3, name: 'Virtual Goods' },
      { id: 4, name: 'Physical Goods' },
    ]);
  }

  public getStatusType(): Observable<any[]> {
    return of(
      [
        { value: 'OFFLINE' },
        { value: 'PAUSA' },
        { value: 'DELETADO' },
        { value: 'TESTE' },
        { value: 'MIGRAÇÃO' },
        { value: 'ONLINE' },
        { value: 'PRÉ-MIGRAÇÃO' },
      ].map(({ value }) => ({ value, id: value, label: value, name: value }))
    );
  }

  public getTemplateOptions(): Observable<Option[]> {
    // BE: Wainting for Endpoint
    return of([
      { id: 'template_downloads', value: 'Downloads', name: 'Downloads' },
      { id: 'template_games', value: 'Games', name: 'Games' },
      { id: 'template_news', value: 'News', name: 'News' },
      { id: 'template_love_dating', value: 'Love & Dating', name: 'Love & Dating' },
      { id: 'template_sports', value: 'Sports', name: 'Sports' },
    ]);
  }

  public getTimeChanges(): Observable<Option[]> {
    const url = `/mock/time-changes`;

    return this.http.get<Option[]>(url).pipe(pluck('responseData'));
  }

  public getUnits(): Observable<Option[]> {
    const url = `/mock/units`;

    return this.http.get<Option[]>(url).pipe(pluck('responseData'));
  }

  public getCampaignTypes() {
    const url = `/venus-api/campaign/type`;
    return this.http.get(url).pipe(
      map((response: any) => {
        response = response.map((type) => ({
          label: type,
          value: type,
          id: type,
          name: type
        }));
        response.unshift({ label: 'QUALQUER', name: 'QUALQUER', id: 'QUALQUER', value: '' });
        return response;
      })
    );
  }
}
